import React, { createContext, useContext, useReducer, ReactNode, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Country } from '../../types';
import { ProcessingResult } from '../../types/client';

export type WizardStep = 'load' | 'crop' | 'download';

export interface SelectionArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface WizardState {
  currentStep: WizardStep;
  uploadedFile: File | null;
  selectedCountry: Country | null;
  processedResult: ProcessingResult | null;
  finalPhoto: Blob | null;
  selectionState: SelectionArea | null;
  completedSteps: WizardStep[];
  isBackgroundRemoved: boolean;
  isBackgroundRemoving: boolean;
}

type WizardAction = 
  | { type: 'SET_STEP'; step: WizardStep }
  | { type: 'SET_FILE'; file: File | null }
  | { type: 'SET_COUNTRY'; country: Country | null }
  | { type: 'SET_PROCESSED_RESULT'; result: ProcessingResult | null }
  | { type: 'SET_FINAL_PHOTO'; photo: Blob | null }
  | { type: 'SET_SELECTION_STATE'; selection: SelectionArea | null }
  | { type: 'COMPLETE_STEP'; step: WizardStep }
  | { type: 'SET_BACKGROUND_REMOVING'; isRemoving: boolean }
  | { type: 'SET_BACKGROUND_REMOVED'; isRemoved: boolean }
  | { type: 'RESET_WIZARD' }
  | { type: 'RESET_PROCESSING_STATE' };

const initialState: WizardState = {
  currentStep: 'load',
  uploadedFile: null,
  selectedCountry: null,
  processedResult: null,
  finalPhoto: null,
  selectionState: null,
  completedSteps: [],
  isBackgroundRemoved: false,
  isBackgroundRemoving: false
};

function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, currentStep: action.step };
    case 'SET_FILE':
      return { ...state, uploadedFile: action.file };
    case 'SET_COUNTRY':
      return { ...state, selectedCountry: action.country };
    case 'SET_PROCESSED_RESULT':
      return { ...state, processedResult: action.result };
    case 'SET_FINAL_PHOTO':
      return { ...state, finalPhoto: action.photo };
    case 'SET_SELECTION_STATE':
      return { ...state, selectionState: action.selection };
    case 'COMPLETE_STEP':
      return {
        ...state,
        completedSteps: state.completedSteps.includes(action.step)
          ? state.completedSteps
          : [...state.completedSteps, action.step]
      };
    case 'SET_BACKGROUND_REMOVING':
      return { ...state, isBackgroundRemoving: action.isRemoving };
    case 'SET_BACKGROUND_REMOVED':
      return { ...state, isBackgroundRemoved: action.isRemoved };
    case 'RESET_WIZARD':
      return initialState;
    case 'RESET_PROCESSING_STATE':
      return {
        ...state,
        processedResult: null,
        finalPhoto: null,
        selectionState: null,
        completedSteps: state.completedSteps.filter(step => step === 'load'), // Keep load step if completed
        isBackgroundRemoved: false,
        isBackgroundRemoving: false
      };
    default:
      return state;
  }
}

export interface WizardContextType {
  state: WizardState;
  goToStep: (step: WizardStep) => void;
  setFile: (file: File | null) => void;
  setCountry: (country: Country | null) => void;
  setProcessedResult: (result: ProcessingResult | null) => void;
  setFinalPhoto: (photo: Blob | null) => void;
  setSelectionState: (selection: SelectionArea | null) => void;
  completeStep: (step: WizardStep) => void;
  setBackgroundRemoving: (isRemoving: boolean) => void;
  setBackgroundRemoved: (isRemoved: boolean) => void;
  resetWizard: () => void;
  resetProcessingState: () => void;
  canNavigateToStep: (step: WizardStep) => boolean;
}

const WizardContext = createContext<WizardContextType | undefined>(undefined);

export const useWizard = () => {
  const context = useContext(WizardContext);
  if (context === undefined) {
    throw new Error('useWizard must be used within a WizardProvider');
  }
  return context;
};

interface WizardProviderProps {
  children: ReactNode;
}

export const WizardProvider: React.FC<WizardProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(wizardReducer, initialState);
  const navigate = useNavigate();
  const location = useLocation();

  // Map URL paths to wizard steps
  const getStepFromPath = (pathname: string): WizardStep => {
    switch (pathname) {
      case '/crop.html':
        return 'crop';
      case '/save.html':
        return 'download';
      default:
        return 'load';
    }
  };

  // Map wizard steps to URL paths
  const getPathFromStep = (step: WizardStep): string => {
    switch (step) {
      case 'crop':
        return '/crop.html';
      case 'download':
        return '/save.html';
      default:
        return '/';
    }
  };

  // Sync URL with wizard state on location change
  useEffect(() => {
    const currentStep = getStepFromPath(location.pathname);
    if (state.currentStep !== currentStep) {
      dispatch({ type: 'SET_STEP', step: currentStep });
    }
  }, [location.pathname, state.currentStep]);

  const goToStep = useCallback((step: WizardStep) => {
    const path = getPathFromStep(step);
    navigate(path);
    dispatch({ type: 'SET_STEP', step });
  }, [navigate]);

  const setFile = useCallback((file: File | null) => {
    dispatch({ type: 'SET_FILE', file });
  }, []);

  const setCountry = useCallback((country: Country | null) => {
    dispatch({ type: 'SET_COUNTRY', country });
  }, []);

  const setProcessedResult = useCallback((result: ProcessingResult | null) => {
    dispatch({ type: 'SET_PROCESSED_RESULT', result });
  }, []);

  const setFinalPhoto = useCallback((photo: Blob | null) => {
    dispatch({ type: 'SET_FINAL_PHOTO', photo });
  }, []);

  const setSelectionState = useCallback((selection: SelectionArea | null) => {
    dispatch({ type: 'SET_SELECTION_STATE', selection });
  }, []);

  const completeStep = useCallback((step: WizardStep) => {
    dispatch({ type: 'COMPLETE_STEP', step });
  }, []);

  const resetWizard = useCallback(() => {
    dispatch({ type: 'RESET_WIZARD' });
  }, []);

  const resetProcessingState = useCallback(() => {
    dispatch({ type: 'RESET_PROCESSING_STATE' });
  }, []);

  const setBackgroundRemoving = useCallback((isRemoving: boolean) => {
    dispatch({ type: 'SET_BACKGROUND_REMOVING', isRemoving });
  }, []);

  const setBackgroundRemoved = useCallback((isRemoved: boolean) => {
    dispatch({ type: 'SET_BACKGROUND_REMOVED', isRemoved });
  }, []);

  const canNavigateToStep = useCallback((step: WizardStep): boolean => {
    if (step === 'load') return true;
    if (step === 'crop') return state.completedSteps.includes('load');
    if (step === 'download') return state.completedSteps.includes('crop');
    return false;
  }, [state.completedSteps]);

  const value: WizardContextType = useMemo(() => ({
    state,
    goToStep,
    setFile,
    setCountry,
    setProcessedResult,
    setFinalPhoto,
    setSelectionState,
    completeStep,
    setBackgroundRemoving,
    setBackgroundRemoved,
    resetWizard,
    resetProcessingState,
    canNavigateToStep
  }), [
    state,
    goToStep,
    setFile,
    setCountry,
    setProcessedResult,
    setFinalPhoto,
    setSelectionState,
    completeStep,
    setBackgroundRemoving,
    setBackgroundRemoved,
    resetWizard,
    resetProcessingState,
    canNavigateToStep
  ]);

  return (
    <WizardContext.Provider value={value}>
      {children}
    </WizardContext.Provider>
  );
};